import React from 'react';
import { FiRefreshCw, FiAlertTriangle, FiCheckCircle, FiClock, FiTarget } from 'react-icons/fi';
import { NodeStatus } from '../../../types/artifactory';
import { useTheme } from '../../../contexts/ThemeContext';

interface HydrationNodeRowProps {
  node: {
    id: string;
    path: string;
    status: NodeStatus;
    dependencies: Array<{
      id: string;
      path: string;
      status: NodeStatus;
    }>;
  };
  isSelected: boolean;
  isCurrentlyHydrating: boolean;
  isCompleted: boolean;
  isFailed: boolean;
  errorMessage?: string;
  nodeStatuses: Record<string, NodeStatus>;
  completedNodes: Set<string>;
  onSelect: () => void;
  onHydrate: () => void;
  isAutoHydrating: boolean;
  isValid: boolean;
  isFocusNode?: boolean;
  onFocusInGraph?: (nodeId: string) => void; // Add this new prop
}

export const HydrationNodeRow: React.FC<HydrationNodeRowProps> = ({
  node,
  isSelected,
  isCurrentlyHydrating,
  isCompleted,
  isFailed,
  errorMessage,
  nodeStatuses,
  completedNodes,
  onSelect,
  onHydrate,
  isAutoHydrating,
  isValid,
  isFocusNode = false,
  onFocusInGraph
}) => {
  const { darkMode } = useTheme();

  // Disable this logging effect
  /*
  React.useEffect(() => {
    console.log(`HydrationNodeRow for ${node.id} (${node.path}):`);
    console.log(`- Dependencies:`, node.dependencies);
    console.log(`- Dependencies length:`, Array.isArray(node.dependencies) ? node.dependencies.length : 'not an array');
    console.log(`- First dependency:`, Array.isArray(node.dependencies) && node.dependencies.length > 0 ? node.dependencies[0] : 'none');
    console.log(`- Is focus node:`, isFocusNode);
  }, []); // Empty dependency array means this only runs once when component mounts
  */

  // Get status color
  const getStatusColor = (status: NodeStatus) => {
    switch (status) {
      case NodeStatus.GHOST: return 'text-gray-400';
      case NodeStatus.PENDING: return 'text-yellow-500';
      case NodeStatus.HYDRATING: return 'text-blue-500';
      case NodeStatus.FRESH: return 'text-green-500';
      case NodeStatus.STALE: return 'text-orange-500';
      case NodeStatus.LOCKED: return 'text-red-500';
      default: return 'text-gray-400';
    }
  };

  // Get status icon
  const getStatusIcon = (status: NodeStatus) => {
    switch (status) {
      case NodeStatus.GHOST: return <FiAlertTriangle />;
      case NodeStatus.PENDING: return <FiClock />;
      case NodeStatus.HYDRATING: return <FiRefreshCw className="animate-spin" />;
      case NodeStatus.FRESH: return <FiCheckCircle />;
      case NodeStatus.STALE: return <FiAlertTriangle />;
      case NodeStatus.LOCKED: return <FiAlertTriangle />;
      default: return <FiAlertTriangle />;
    }
  };

  return (
    <tr
      className={`${
        isSelected
          ? darkMode ? 'bg-blue-900 bg-opacity-20' : 'bg-blue-50'
          : ''
      } hover:bg-opacity-10 ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
    >
      <td className="p-1 text-center">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          disabled={isAutoHydrating}
          className="accent-blue-600 h-3 w-3 cursor-pointer"
        />
      </td>
      <td className="p-1 font-mono text-xs">
        {/* Show if this is the focus node */}
        {isFocusNode && (
          <span className="inline-block mr-1 px-1 text-xs rounded bg-blue-500 text-white">Focus</span>
        )}
        <div className="flex items-start">
          {/* Add focus button */}
          {onFocusInGraph && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onFocusInGraph(node.id);
              }}
              className={`mr-1 p-0.5 rounded-full flex-shrink-0 ${
                darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-200'
              } transition-colors`}
              title="Focus on this node in the graph"
            >
              <FiTarget className="h-3 w-3 text-blue-500" />
            </button>
          )}
          <span className="break-words font-mono text-xs" style={{ wordBreak: 'break-word' }}>
            {node.path.split('/').map((part, index, array) => (
              <React.Fragment key={index}>
                {index > 0 && <wbr />}
                {part}
                {index < array.length - 1 && '/'}
              </React.Fragment>
            ))}
          </span>
        </div>
      </td>
      <td className="p-1">
        <div className={`flex items-center ${getStatusColor(nodeStatuses[node.id] || node.status)}`}>
          {getStatusIcon(nodeStatuses[node.id] || node.status)}
          <span className="ml-1 text-xs">
            {nodeStatuses[node.id] || node.status}
          </span>
        </div>
      </td>
      <td className="p-1">
        <div className="flex flex-col gap-0.5">
          {Array.isArray(node.dependencies) && node.dependencies.length > 0 ? (
            <div className="flex flex-wrap gap-0.5">
              {node.dependencies.map((dep) => (
                <span
                  key={dep.id}
                  className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                    completedNodes.has(dep.id) || dep.status === 'fresh'
                      ? darkMode ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-800'
                      : dep.status === 'stale'
                        ? darkMode ? 'bg-orange-900 bg-opacity-30 text-orange-400' : 'bg-orange-100 text-orange-800'
                        : dep.status === 'ghost'
                          ? darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                          : darkMode ? 'bg-blue-900 bg-opacity-30 text-blue-400' : 'bg-blue-100 text-blue-800'
                  }`}
                  title={`${dep.path} (${dep.status})`}
                >
                  <span className="truncate max-w-[80px] font-mono">{dep.path.split('/').pop() || dep.path}</span>
                  {(!dep.status || dep.status !== 'fresh') && !completedNodes.has(dep.id) && (
                    <span className="ml-1 w-1.5 h-1.5 rounded-full bg-red-500" title="Needs hydration"></span>
                  )}
                </span>
              ))}
            </div>
          ) : (
            <span className="text-xs text-gray-500">
              None
            </span>
          )}
        </div>
      </td>
      <td className="p-1">
        <div className="flex flex-col">
          <button
            className={`px-1.5 py-0.5 rounded text-xs font-medium ${
              isCurrentlyHydrating
                ? 'bg-blue-600 text-white cursor-wait'
                : isCompleted
                  ? darkMode ? 'bg-green-900 bg-opacity-30 text-green-400' : 'bg-green-100 text-green-800'
                  : isFailed
                    ? darkMode ? 'bg-red-700 hover:bg-red-600 text-white' : 'bg-red-100 hover:bg-red-200 text-red-800'
                    : darkMode ? 'bg-blue-700 hover:bg-blue-600 text-white' : 'bg-blue-100 hover:bg-blue-200 text-blue-800'
            }`}
            onClick={onHydrate}
            disabled={isCurrentlyHydrating || isAutoHydrating || !isValid}
          >
            {!isValid ? (
              'Invalid'
            ) : isCurrentlyHydrating ? (
              <FiRefreshCw className="animate-spin" />
            ) : isCompleted ? (
              'Done'
            ) : isFailed ? (
              'Retry'
            ) : nodeStatuses[node.id] === 'ghost' || node.status === 'ghost' ? (
              'Hydrate'
            ) : (
              'Rehydrate'
            )}
          </button>
          
          {/* Show error message if there is one */}
          {errorMessage && (
            <div className="mt-0.5 text-xs text-red-500 max-w-[100px] truncate" title={errorMessage}>
              Error: {errorMessage.substring(0, 15)}
              {errorMessage.length > 15 ? '...' : ''}
            </div>
          )}
        </div>
      </td>
    </tr>
  );
};
